/**
 * E: Velocity economics - quote-to-cash, book-to-bill, months of
 * cover. WiP pounds + Schedule of Work dates, joined on CID.
 */
import {
  ComposedChart, Bar, Line, XAxis, YAxis, ResponsiveContainer, Legend,
  Tooltip as RechartsTooltip,
} from "recharts";
import { fmtGbpShort, PanelTitle } from "../staff/CommissionedPanels";
import { useCfoData, TabState, KpiStrip, Card, SourceBadge, InfoTip } from "./cfoShared";

export default function VelocityTab() {
  const { data, loading, error } = useCfoData("cfo/velocity");
  if (loading || error) return <TabState loading={loading} error={error} testid="cfo-velocity" />;

  const q2c = data.quote_to_cash;
  const cover = data.cover;
  const trend = q2c.by_year;
  const trendDelta = trend.length >= 2 ? trend[trend.length - 1].median_days - trend[0].median_days : null;

  return (
    <div className="flex flex-col gap-4" data-testid="cfo-velocity">
      <KpiStrip cards={[
        { label: "Quote → first cash · median", value: q2c.median_days != null ? `${q2c.median_days}d` : "-", tone: "text-[#1B4F8A]", testid: "velocity-kpi-q2c", info: "vel_q2c", sub: `${q2c.sample} projects measured${trendDelta != null ? ` · ${trendDelta > 0 ? "slowing" : "speeding up"} (${trend[0].year}: ${trend[0].median_days}d → ${trend[trend.length - 1].year}: ${trend[trend.length - 1].median_days}d)` : ""}` },
        { label: "Months of cover", value: cover.months_of_cover != null ? cover.months_of_cover : "-", tone: cover.months_of_cover != null && cover.months_of_cover < 4 ? "text-rose-700" : "text-teal-700", testid: "velocity-kpi-cover", info: "vel_cover", sub: "forward book ÷ run-rate" },
        { label: "Monthly run-rate · last 6m", value: fmtGbpShort(cover.monthly_run_rate), tone: "text-slate-900", testid: "velocity-kpi-runrate", info: "vel_runrate" },
        { label: "Forward contracted", value: fmtGbpShort(cover.forward_contracted), tone: "text-[#1B4F8A]", testid: "velocity-kpi-forward", info: "vel_forward" },
      ]} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card testid="velocity-book-to-bill" className="lg:col-span-2">
          <div className="flex items-center justify-between mb-1">
            <PanelTitle>Book-to-bill per quarter <InfoTip id="vel_b2b" /></PanelTitle>
            <SourceBadge kind="joined" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data.book_to_bill} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="quarter" tick={{ fontSize: 9, fill: "#a8a29e" }} axisLine={false} tickLine={false} interval={1} />
                <YAxis yAxisId="v" tickFormatter={(v) => fmtGbpShort(v)} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={54} />
                <YAxis yAxisId="r" orientation="right" domain={[0, 3]} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={30} />
                <RechartsTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v, n) => n === "ratio" ? [v, "book-to-bill"] : [fmtGbpShort(v), n === "booked" ? "new bookings" : "billed"]} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar yAxisId="v" dataKey="booked" fill="#1B4F8A" maxBarSize={16} radius={[2, 2, 0, 0]} />
                <Bar yAxisId="v" dataKey="billed" fill="#0D9488" fillOpacity={0.7} maxBarSize={16} radius={[2, 2, 0, 0]} />
                <Line yAxisId="r" dataKey="ratio" stroke="#F59E0B" strokeWidth={1.5} dot={{ r: 2 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[10px] text-stone-400 mt-2">
            Bookings = Schedule of Work commission value per quarter; billed = WiP ledger. Ratio above 1 = the book is growing. Last quarter is partial.
          </p>
        </Card>

        <Card testid="velocity-q2c-teams">
          <PanelTitle>Quote → first cash by team <InfoTip id="vel_q2c_team" /></PanelTitle>
          <ul className="space-y-2">
            {q2c.by_team.filter((t) => t.sample >= 5).map((t) => {
              const max = Math.max(...q2c.by_team.map((x) => x.median_days || 0), 1);
              return (
                <li key={t.team} data-testid={`velocity-q2c-${t.team}`}>
                  <div className="flex items-center justify-between text-xs mb-0.5">
                    <span className="font-medium text-slate-800">{t.team}</span>
                    <span className="font-mono tabular-nums text-stone-600">{t.median_days}d · {t.sample} projects</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-stone-100">
                    <div className="h-full rounded-full bg-gradient-to-r from-[#1B4F8A] to-[#3EA8DC]" style={{ width: `${Math.max((t.median_days / max) * 100, 2)}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
          <p className="text-[10px] font-mono uppercase tracking-wider text-stone-500 mt-4 mb-1">By year of first invoice</p>
          <ul className="space-y-1">
            {q2c.by_year.map((y) => (
              <li key={y.year} className="flex items-center justify-between text-xs">
                <span className="font-mono text-slate-800">{y.year}</span>
                <span className="font-mono tabular-nums text-stone-600">{y.median_days}d · {y.sample}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
